import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ACCESS_KEY } from "../config";

const BDAdmin = () => {
  // Lists and UI states
  const [bdAdmins, setBdAdmins] = useState([]); 
  const [admins, setAdmins] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Form States (Add New)
  const [selectedAdmin, setSelectedAdmin] = useState(''); 
  const [searchUniqueId, setSearchUniqueId] = useState('');
  const [fetchedUser, setFetchedUser] = useState(null); 
  const [searchMessage, setSearchMessage] = useState('');
  
  // Manual Input States (Common for Add/Edit)
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [accountNo, setAccountNo] = useState('');
  const [codeNo, setCodeNo] = useState(''); 
  const [selectedId, setSelectedId] = useState(''); // Edit ke liye ID

  // 1. Load Initial Data (BD Admins and Parent Admins)
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const bdRes = await axios.get(`https://admin.butterlive.co.in/management/getBDAdmins?start=1&limit=50&t=${Date.now()}`, {
        headers: { key: ACCESS_KEY }
      });
      if (bdRes.data.status) {
        setBdAdmins(bdRes.data.data);
      }

      const adminRes = await axios.get(`https://admin.butterlive.co.in/management/getAdminUsers?start=1&limit=100`, {
        headers: { key: ACCESS_KEY }
      });
      if (adminRes.data.status) {
        setAdmins(adminRes.data.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // 2. MongoDB User Verification
  const handleUserSearch = async () => {
    if (!searchUniqueId) return alert("Please enter Unique ID first");
    setSearchMessage("Searching in MongoDB...");
    setFetchedUser(null);

    try {
      const response = await axios.get(`https://admin.butterlive.co.in/management/search-device-user?uniqueId=${searchUniqueId}`, {
        headers: { key: ACCESS_KEY }
      });
      if (response.data.status) {
        setFetchedUser(response.data.data);
        setSearchMessage("");
      } else {
        setSearchMessage("User not found in MongoDB!");
      }
    } catch (error) {
      setSearchMessage("Error fetching user data");
    }
  };

  // 3. Create New BD Admin
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAdmin) return alert("Please select an Admin first!");
    if (!fetchedUser) return alert("Please verify Unique ID from MongoDB first!");
    if (!phone || !email || !accountNo || !codeNo) return alert("Please fill all fields");

    try {
      const response = await axios.post(`https://admin.butterlive.co.in/management/addBDAdmin`, {
        adminId: selectedAdmin,
        userId: fetchedUser._id || fetchedUser.id || fetchedUser.userId,
        uniqueId: searchUniqueId,
        codeNo: codeNo.toUpperCase().trim(), 
        phone: phone.trim(),
        email: email.trim(),
        accountNo: accountNo.trim()
      }, {
        headers: { key: ACCESS_KEY }
      });

      if (response.data.status) {
        alert(response.data.message || "BD Admin Created!");
        handleCloseModal();
        fetchInitialData(); 
      } else {
        alert(response.data.message || "Something went wrong");
      }
    } catch (error) {
      alert(error.response?.data?.message || "Error saving BD admin");
    }
  };

  // 🔥 4. Toggle Status (Active/Inactive)
  const handleToggleStatus = async (id) => {
    if (!window.confirm("Are you sure you want to change this BD Admin's status?")) return;
    try {
      const response = await axios.post(`https://admin.butterlive.co.in/management/toggleRoleStatus`, {
        id,
        role: "bd"
      }, {
        headers: { key: ACCESS_KEY }
      });

      if (response.data.status) {
        alert(response.data.message);
        fetchInitialData(); // Refresh list
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      alert("Error updating status");
    }
  };

  // 🔥 5. Open Edit Modal with Pre-fill
  const handleOpenEdit = (bd) => {
    setSelectedId(bd._id);
    setSelectedAdmin(bd.adminId || bd.admin?._id || bd.admin || '');
    setPhone(bd.phone);
    setEmail(bd.email);
    setAccountNo(bd.accountNo);
    setShowEditModal(true);
  };

  // 🔥 6. Submit Updated Details
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!phone || !email) return alert("Phone and Email are required");

    try {
      const response = await axios.post(`https://admin.butterlive.co.in/management/updateRoleDetails`, {
        id: selectedId,
        role: "bd",
        phone: phone.trim(),
        email: email.trim(),
        accountNo: accountNo.trim(),
        adminId: selectedAdmin
      }, {
        headers: { key: ACCESS_KEY }
      });

      if (response.data.status) {
        alert(response.data.message || "BD Admin Details Updated!");
        setShowEditModal(false);
        clearForm();
        fetchInitialData(); 
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      alert("Error updating details");
    }
  };

  const clearForm = () => {
    setSelectedAdmin('');
    setSearchUniqueId('');
    setFetchedUser(null);
    setPhone('');
    setEmail('');
    setAccountNo('');
    setCodeNo('');
    setSearchMessage('');
    setSelectedId('');
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    clearForm();
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">BD Admin Management</h1>
      </div>

      <div className="top-bar">
        <input type="text" placeholder="Search BD Admin..." className="search-box" />
        <button onClick={() => setShowAddModal(true)} className="ban-btn ban-btn-unban" style={{ height: '40px', padding: '0 25px' }}>
          + New BD Admin
        </button>
      </div>

      <div className="table-card">
        <table width="100%" cellPadding="12">
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Photo</th>
              <th>Name</th>
              <th>Unique ID</th>
              <th>Code No</th>
              <th>Mapped Admin</th>
              <th>Mobile</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="no-data">Loading BD Admins...</td></tr>
            ) : bdAdmins.length === 0 ? (
              <tr><td colSpan="9" className="no-data">No BD Admin created yet.</td></tr>
            ) : (
              bdAdmins.map((bd, index) => (
                <tr key={bd._id || index}>
                  <td>{index + 1}</td>
                  <td>
                    <img src={bd.image || "https://placehold.co/50"} alt="" className="user-avatar" onError={(e) => { e.target.src = "https://placehold.co/50" }} />
                  </td>
                  <td>{bd.name}</td>
                  <td>{bd.uniqueId}</td>
                  <td style={{ color: '#ff4d94', fontWeight: 'bold' }}>{bd.codeNo}</td>
                  <td>
                    <span style={{ color: '#9ca3af' }}>{bd.adminName}</span> 
                    <span style={{ color: '#4f46e5', marginLeft: '5px', fontWeight: 'bold' }}>({bd.adminCode})</span>
                  </td>
                  <td>{bd.phone}</td>
                  {/* Status Toggle on Click */}
                  <td onClick={() => handleToggleStatus(bd._id)} style={{ cursor: 'pointer' }}>
                    <span className={bd.isActive ? "status-active" : "status-banned"}>
                      {bd.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => handleOpenEdit(bd)} className="ban-btn" style={{ background: '#4f46e5' }}>Edit</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ADD MODAL */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ width: '450px' }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#fff' }}>Create New BD Admin</h3>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Select Admin (Parent)</label>
              <select value={selectedAdmin} onChange={(e) => setSelectedAdmin(e.target.value)} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }}>
                <option value="">-- Choose Admin --</option>
                {admins.map((admin) => (
                  <option key={admin._id} value={admin._id}>{admin.name} ({admin.codeNo})</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '15px', background: '#0f172a', padding: '15px', borderRadius: '8px', border: '1px solid #1f2937' }}>
              <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Verify User (Unique ID)</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input type="number" placeholder="Enter User Unique ID" value={searchUniqueId} onChange={(e) => setSearchUniqueId(e.target.value)} style={{ flex: 1, padding: '10px', background: '#111827', color: 'white', border: '1px solid #374151', borderRadius: '6px', outline: 'none' }} />
                <button type="button" onClick={handleUserSearch} className="ban-btn ban-btn-active" style={{ background: '#4f46e5' }}>Verify</button>
              </div>
              {searchMessage && <p style={{ fontSize: '12px', color: '#ef4444', margin: '5px 0 0 0' }}>{searchMessage}</p>}
              {fetchedUser && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px', background: '#111827', padding: '10px', borderRadius: '6px' }}>
                  <img src={fetchedUser.image || "https://placehold.co/40"} alt="" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#22c55e' }}>✔ User Verified</div>
                    <div style={{ fontSize: '13px', color: '#fff' }}>{fetchedUser.name}</div>
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={handleCreateSubmit}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Code No</label>
                <input type="text" placeholder="e.g., BD0033" value={codeNo} onChange={(e) => setCodeNo(e.target.value)} maxLength={6} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Phone Number</label>
                <input type="text" placeholder="Enter Phone" value={phone} onChange={(e) => setPhone(e.target.value)} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Email Address</label>
                <input type="email" placeholder="Enter Email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Bank Account No</label>
                <input type="text" placeholder="Enter Account No" value={accountNo} onChange={(e) => setAccountNo(e.target.value)} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={handleCloseModal} style={{ background: '#374151', border: '1px solid #4b5563' }}>Cancel</button>
                <button type="submit" className="ban-btn ban-btn-unban">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ width: '450px' }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#fff' }}>Edit BD Admin Details</h3>
            <form onSubmit={handleEditSubmit}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Assign Admin</label>
                <select
                  value={selectedAdmin}
                  onChange={(e) => setSelectedAdmin(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: '#0f172a',
                    color: 'white',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                >
                  <option value="">-- Choose Admin --</option>
                  {admins.map((admin) => (
                    <option key={admin._id} value={admin._id}>
                      {admin.name} ({admin.codeNo})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Phone Number</label>
                <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Email Address</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Bank Account No</label>
                <input type="text" value={accountNo} onChange={(e) => setAccountNo(e.target.value)} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={handleCloseModal} style={{ background: '#374151', border: '1px solid #4b5563' }}>Cancel</button>
                <button type="submit" className="ban-btn ban-btn-unban" style={{ background: '#22c55e' }}>Update Details</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BDAdmin;
